/**
 * Forgot Password page — Phase 2L-A12.
 *
 * Public route. Sends a POST /api/auth/forgot-password and always
 * shows a generic success card regardless of whether the email
 * matched a real account (anti-enumeration).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BrandLogo } from "@/components/BrandLogo";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSubmitted(true);
    } catch (err) {
      // The backend is designed to ALWAYS 200 — if we land here it's
      // a 422 validation issue (e.g. malformed email).
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send reset email.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <BrandLogo to="/" size="sm" />
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600">Account recovery</div>
            <h1 className="font-display mt-2 text-3xl lg:text-4xl font-bold text-slate-900">Forgot your password?</h1>
            <p className="mt-2 text-slate-600">
              Enter your email and we&apos;ll send you a link to choose a new password.
            </p>
          </div>

          {submitted ? (
            <div
              data-testid="forgot-password-success-card"
              className="bg-white rounded-3xl border border-slate-200 p-7 shadow-sm space-y-3"
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h2 className="font-display text-lg font-semibold text-slate-900">Check your inbox</h2>
                  <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">
                    If an account exists for <strong className="text-slate-900">{email}</strong>, a password
                    reset link is on its way. The link expires in 30 minutes and can only be used once.
                  </p>
                  <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                    Didn&apos;t receive an email after a few minutes? Check your spam folder, or
                    return here to request another link.
                  </p>
                </div>
              </div>
              <div className="pt-3 border-t border-slate-100">
                <Link
                  to="/login"
                  data-testid="forgot-password-back-to-login"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-600 hover:underline"
                >
                  <ArrowLeft className="w-4 h-4" /> Back to sign in
                </Link>
              </div>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="forgot-password-form"
              className="bg-white rounded-3xl border border-slate-200 p-7 shadow-sm space-y-4"
            >
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Email</Label>
                <Input
                  data-testid="forgot-password-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-1.5 h-12 rounded-xl"
                />
              </div>
              <Button
                data-testid="forgot-password-submit"
                disabled={submitting}
                type="submit"
                size="lg"
                className="w-full btn-aqua rounded-full font-semibold mt-2"
              >
                {submitting
                  ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Sending…</>
                  : <>Send reset link <ArrowRight className="w-4 h-4 ml-1.5" /></>}
              </Button>
              <p className="text-center text-xs text-slate-500 pt-2">
                <Link to="/login" data-testid="forgot-password-cancel" className="font-semibold text-slate-600 hover:underline">
                  ← Back to sign in
                </Link>
              </p>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
